import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/format";
import { VegDot } from "@/components/common/Bits";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PaymentDialog from "@/components/common/PaymentDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  Utensils,
  ShoppingBag,
  Bike,
  X,
  Receipt,
  Loader2,
} from "lucide-react";

const TYPES = [
  { key: "dine_in", label: "Dine-In", icon: Utensils },
  { key: "takeaway", label: "Takeaway", icon: ShoppingBag },
  { key: "delivery", label: "Delivery", icon: Bike },
];
const MODES = ["Cash", "UPI", "Razorpay"];

// POS billing must preserve paise until the final payment step.
// Show decimals only when the amount actually has them.
const moneyExact = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export default function POS() {
  const location = useLocation();
  const navigate = useNavigate();
  const { restaurant, branchId, branches, subscribe } = useApp();
  const isStarterPlan =
    String(
      restaurant?.plan || restaurant?.subscription?.plan || "",
    ).toLowerCase() === "starter";

  useEffect(() => {
    if (location.state?.existingOrder) {
      const order = location.state.existingOrder;

      setExistingOrder(order);
      setAddItemMode(Boolean(location.state.addItemMode));

      if (order.table_id) {
        setType("dine_in");
        setTableId(order.table_id);
      }

      if (order.customer_name) {
        setCustName(order.customer_name);
      }

      if (order.customer_phone) {
        setCustPhone(order.customer_phone);
      }
    }

    if (location.state?.tableId) {
      setType("dine_in");
      setTableId(location.state.tableId);
    }
  }, [location.state]);
  const activeBranch = branchId === "all" ? branches[0]?.id : branchId;
  const activeBranchDetails = branches.find(
    (branch) => String(branch.id) === String(activeBranch),
  );

  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [type, setType] = useState("dine_in");
  const [tableId, setTableId] = useState("");
  const [tables, setTables] = useState([]);
  const [billDiscount, setBillDiscount] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [existingOrder, setExistingOrder] = useState(null);
  const [addItemMode, setAddItemMode] = useState(false);
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [source, setSource] = useState("pos");
  const [settings, setSettings] = useState({
    payment_discounts: {},
    gst_rate: 0,
    gst_enabled: false,
    upi_id: "",
  });

  useEffect(() => {
    api
      .categories()
      .then((r) => setCats(r.data))
      .catch(() => {});
    api
      .settings()
      .then((r) => setSettings(r.data.settings))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (activeBranch) {
      api
        .menuItems({ branch_id: activeBranch })
        .then((r) => setItems(r.data))
        .catch(() => {});
      api
        .tables(activeBranch)
        .then((r) => setTables(r.data))
        .catch(() => {});
    }
  }, [activeBranch]);

  useEffect(() => {
    if (!activeBranch || !subscribe) return;

    const unsubscribe = subscribe("table.update", (updatedTable) => {
      console.log("TABLE UPDATE RECEIVED:", updatedTable);
      if (!updatedTable?.id) return;

      setTables((currentTables) => {
        const exists = currentTables.some(
          (table) => table.id === updatedTable.id,
        );

        if (!exists) {
          return [...currentTables, updatedTable];
        }

        return currentTables.map((table) =>
          table.id === updatedTable.id ? { ...table, ...updatedTable } : table,
        );
      });
    });

    return unsubscribe;
  }, [activeBranch, subscribe]);

  const filtered = useMemo(
    () =>
      items.filter(
        (i) =>
          (activeCat === "all" || i.category_id === activeCat) &&
          (!q || i.name.toLowerCase().includes(q.toLowerCase())),
      ),
    [items, activeCat, q],
  );

  const addItemsToExistingOrder = async () => {
    if (!existingOrder || !cart.length) return;

    setPlacing(true);

    try {
      await api.addOrderItems(existingOrder.id, cart);

      toast.success(`Items added to Order #${existingOrder.order_no}`);

      setExistingOrder(null);
      setAddItemMode(false);
      resetCart();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add items");
    } finally {
      setPlacing(false);
    }
  };

  const addToCart = (item) => {
    if (!item.available) return;
    setCart((c) => {
      const ex = c.find((x) => x.menu_item_id === item.id);
      if (ex)
        return c.map((x) =>
          x.menu_item_id === item.id ? { ...x, qty: x.qty + 1 } : x,
        );
      return [
        ...c,
        {
          menu_item_id: item.id,
          name: item.name,
          price: item.price,
          qty: 1,
          veg: item.veg,
          station: item.station || "Main Kitchen",
          modifiers: [],
          notes: "",
          discount: 0,
        },
      ];
    });
  };
  const changeQty = (id, delta) =>
    setCart((c) =>
      c
        .map((x) =>
          x.menu_item_id === id ? { ...x, qty: Math.max(0, x.qty + delta) } : x,
        )
        .filter((x) => x.qty > 0),
    );
  const removeItem = (id) =>
    setCart((c) => c.filter((x) => x.menu_item_id !== id));

  const subtotal = cart.reduce((s, x) => s + x.price * x.qty, 0);

  const taxable = Math.max(0, subtotal - billDiscount);

  const gstRate =
    settings.gst_enabled === true ? Number(settings.gst_rate ?? 0) / 100 : 0;

  // Keep GST and bill total at 2-decimal precision.
  // Do NOT round ₹73.50 to ₹74 here. Final whole-rupee rounding,
  // if required, belongs to the final payment/settlement step.
  const tax = Math.round(taxable * gstRate * 100) / 100;
  const grossTotal = Math.round((taxable + tax) * 100) / 100;
  const total = grossTotal;

  const buildBody = (extraDiscount = 0) => ({
    branch_id: activeBranch,
    type,
    table_id: isStarterPlan ? null : type === "dine_in" ? tableId : null,
    customer_name: custName || null,
    customer_phone: custPhone || null,
    items: cart,
    bill_discount: (Number(billDiscount) || 0) + extraDiscount,
    tax_rate:
      settings.gst_enabled === true ? Number(settings.gst_rate ?? 0) : 0,
    source,
  });

  const validate = () => {
    // Existing order billing does not need cart items
    if (existingOrder) return true;

    if (!cart.length) {
      toast.error("Cart is empty");
      return false;
    }

    if (type === "dine_in" && !tableId && !isStarterPlan) {
      toast.error("Select a table for dine-in");
      return false;
    }

    return true;
  };

  const sendToKitchen = async () => {
    if (!cart.length) {
      toast.error("Add at least one item");
      return false;
    }

    if (!validate()) {
      return false;
    }

    // Existing order → add new items to the same order
    if (existingOrder) {
      return await addItemsToExistingOrder();
    }

    // Delivery validation
    if (type === "delivery") {
      if (!custName.trim()) {
        toast.error("Customer name is required for delivery");
        return false;
      }

      if (custPhone.length !== 10) {
        toast.error("Valid 10-digit mobile number is required for delivery");
        return false;
      }
    }

    // Optional phone validation for Takeaway
    if (type === "takeaway" && custPhone && custPhone.length !== 10) {
      toast.error("Mobile number must be 10 digits");
      return false;
    }

    try {
      const r = await api.createOrder(buildBody());

      if (r?.data?.id) {
        toast.success("Sent to kitchen");
        setCart([]);
        setCustName("");
        setCustPhone("");
        return true;
      }

      toast.error("Failed to send order");
      return false;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send order");
      return false;
    }
  };

  const billExistingOrder = async () => {
    if (!existingOrder) return;

    setPlacing(true);

    try {
      let order = existingOrder;

      // Agar naye items cart me hain, pehle same existing order me add karo
      if (cart.length) {
        const r = await api.addOrderItems(existingOrder.id, cart);
        order = r.data;

        setExistingOrder(order);
        resetCart();
      }

      // Updated existing order ke total ke saath payment kholo
      setPayOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update order");
    } finally {
      setPlacing(false);
    }
  };

  const resetCart = () => {
    setCart([]);
    setBillDiscount(0);
    setTableId("");
    setCustName("");
    setCustPhone("");
  };

  const existingItemIds = new Set(
    (existingOrder?.items || []).map((item) => item.menu_item_id),
  );
  const existingOrderTotal = existingOrder?.total || 0;

  const existingOrderServed = existingOrder?.status === "served";

  const canBillExistingOrder =
    Boolean(existingOrder) && existingOrderServed && cart.length === 0;

  const availableTables = tables.filter(
    (t) => t.status === "available" || t.id === tableId,
  );

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] overflow-hidden bg-stone-100">
      {/* Menu area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="p-4 bg-white border-b border-border">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search menu items…"
              className="pl-10 h-11 rounded-xl"
              data-testid="pos-search"
            />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar pb-1">
            <CatBtn
              active={activeCat === "all"}
              onClick={() => setActiveCat("all")}
            >
              All
            </CatBtn>
            {cats.map((c) => (
              <CatBtn
                key={c.id}
                active={activeCat === c.id}
                onClick={() => setActiveCat(c.id)}
              >
                {c.name}
              </CatBtn>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-24 lg:pb-4">
          {items.length === 0 ? (
            <div
              className="h-full min-h-[420px] flex items-center justify-center"
              data-testid="pos-empty-menu"
            >
              <div className="text-center max-w-sm">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-stone-100 grid place-items-center mb-4">
                  <Utensils size={28} className="text-muted-foreground" />
                </div>
                <h3 className="font-display text-lg font-bold">
                  No menu items available
                </h3>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Add menu items to start taking orders.
                </p>
                <Button
                  type="button"
                  onClick={() => navigate("/app/menu")}
                  className="mt-4 rounded-xl font-semibold"
                  data-testid="pos-add-menu-item"
                >
                  <Plus size={16} />
                  Add Menu Item
                </Button>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="h-full min-h-[420px] flex items-center justify-center"
              data-testid="pos-no-results"
            >
              <div className="text-center max-w-sm">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-stone-100 grid place-items-center mb-4">
                  <Search size={28} className="text-muted-foreground" />
                </div>
                <h3 className="font-display text-lg font-bold">
                  No items found
                </h3>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Try a different search or category.
                </p>
              </div>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3"
              data-testid="pos-menu-grid"
            >
              {filtered.map((item) => (
                <button
                  key={item.id}
                  onClick={() => addToCart(item)}
                  disabled={!item.available}
                  data-testid={`menu-item-${item.id}`}
                  className={cn(
                    "group text-left rounded-2xl border overflow-hidden transition-all active:scale-[0.97]",
                    existingOrder && existingItemIds.has(item.id)
                      ? "border-primary ring-2 ring-primary/20 bg-orange-50"
                      : "bg-white border-border",
                    item.available
                      ? "hover:border-primary hover:shadow-[0_8px_24px_rgba(28,25,23,0.1)]"
                      : "opacity-55 cursor-not-allowed",
                  )}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <VegDot
                        veg={item.veg}
                        className="bg-white/90 backdrop-blur"
                      />
                    </div>
                    {!item.available && (
                      <div className="absolute inset-0 bg-white/40 grid place-items-center">
                        <span className="text-xs font-bold text-red-600 bg-white px-2 py-1 rounded-full">
                          Out of stock
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-semibold leading-tight line-clamp-2 min-h-[2.5rem]">
                      {item.name}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-display font-bold text-primary">
                        {money(item.price)}
                      </span>
                      <span className="h-6 w-6 rounded-lg bg-accent text-primary grid place-items-center group-hover:bg-primary group-hover:text-white transition-colors">
                        <Plus size={15} />
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cart */}

      {/* Mobile Cart Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="w-full flex items-center justify-between rounded-xl bg-primary text-white px-4 py-3"
        >
          <div className="flex items-center gap-2">
            <Receipt size={18} />
            <span className="font-semibold">
              Cart ({cart.reduce((sum, item) => sum + item.qty, 0)})
            </span>
          </div>

          <span className="font-bold">{moneyExact(total)}</span>
        </button>
      </div>

      <div
        className={`bg-white flex-col lg:relative lg:w-[380px] lg:shrink-0 lg:border-l lg:border-border ${
          cartOpen
            ? "fixed inset-x-0 bottom-0 z-50 flex h-[75vh] rounded-t-2xl border-t"
            : "hidden"
        } lg:flex`}
        data-testid="pos-cart"
      >
        <div className="lg:hidden flex justify-end px-4 pt-2">
          <button
            type="button"
            onClick={() => setCartOpen(false)}
            className="text-gray-500 hover:text-black text-xl"
            aria-label="Close cart"
          >
            ✕
          </button>
        </div>
        <div className="p-4 border-b border-border">
          <div className="flex gap-1.5 mb-2">
            {[["pos", "Counter / POS"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setSource(k)}
                data-testid={`order-source-${k}`}
                className={cn(
                  "flex-1 rounded-lg py-1.5 text-xs font-semibold border transition-colors",
                  source === k
                    ? "bg-foreground text-white border-foreground"
                    : "bg-secondary/60 border-transparent hover:bg-secondary",
                )}
              >
                {l}
              </button>
            ))}
          </div>
          <div
            className={cn(
              "grid gap-2",
              TYPES.filter((t) => {
                if (isStarterPlan && t.key === "delivery") return false;

                return (
                  t.key !== "delivery" ||
                  settings.availability_mode !== "dine_in_only"
                );
              }).length === 2
                ? "grid-cols-2"
                : "grid-cols-3",
            )}
          >
            {TYPES.filter((t) => {
              if (isStarterPlan && t.key === "delivery") return false;

              return (
                t.key !== "delivery" || settings.availability_mode === "hybrid"
              );
            }).map((t) => (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                data-testid={`order-type-${t.key}`}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-xl py-2.5 text-xs font-semibold border transition-all",
                  type === t.key
                    ? "bg-primary text-white border-primary shadow-[0_4px_12px_rgba(234,88,12,0.3)]"
                    : "bg-secondary/60 text-foreground border-transparent hover:bg-secondary",
                )}
              >
                <t.icon size={18} />
                {isStarterPlan && t.key === "dine_in"
                  ? "Direct POS Billing"
                  : t.label}
              </button>
            ))}
          </div>
          {!isStarterPlan && type === "dine_in" && (
            <select
              value={tableId}
              onChange={(e) => setTableId(e.target.value)}
              data-testid="pos-table-select"
              className="mt-3 w-full h-10 rounded-xl border border-border bg-white px-3 text-sm"
            >
              <option value="">Select table…</option>
              {availableTables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {t.floor} · {t.seats} seats
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {existingOrder && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700">
                    Existing Order
                  </p>
                  <p className="text-sm font-bold text-foreground">
                    Order #{existingOrder.order_no}
                  </p>
                </div>
                <span className="font-bold text-amber-700">
                  {moneyExact(existingOrderTotal)}
                </span>
              </div>

              <div className="space-y-1">
                {(existingOrder.items || []).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-foreground">
                      {item.name} ×{item.qty}
                    </span>
                    <span className="font-semibold">
                      {money(item.price * item.qty)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {cart.length === 0 && (
            <div className="h-full grid place-items-center text-center text-muted-foreground">
              <div>
                <Receipt size={40} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Tap items to add to the order</p>
              </div>
            </div>
          )}
          {cart.map((x) => (
            <div
              key={x.menu_item_id}
              className="flex items-center gap-2 rounded-xl border border-border p-2.5"
              data-testid={`cart-item-${x.menu_item_id}`}
            >
              <VegDot veg={x.veg} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{x.name}</p>
                <p className="text-xs text-muted-foreground">
                  {money(x.price)}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => changeQty(x.menu_item_id, -1)}
                  className="h-7 w-7 rounded-lg bg-secondary grid place-items-center hover:bg-stone-200"
                  data-testid={`cart-minus-${x.menu_item_id}`}
                >
                  <Minus size={14} />
                </button>
                <span className="w-6 text-center text-sm font-bold">
                  {x.qty}
                </span>
                <button
                  onClick={() => changeQty(x.menu_item_id, 1)}
                  className="h-7 w-7 rounded-lg bg-accent text-primary grid place-items-center hover:bg-primary hover:text-white"
                  data-testid={`cart-plus-${x.menu_item_id}`}
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={() => removeItem(x.menu_item_id)}
                className="text-muted-foreground hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-4 space-y-2">
          {(type === "takeaway" || type === "delivery") && (
            <div className="flex gap-2">
              <Input
                value={custName}
                onChange={(e) => setCustName(e.target.value)}
                placeholder="Customer name"
                className="h-9 rounded-lg text-sm"
                data-testid="pos-cust-name"
              />
              <Input
                value={custPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setCustPhone(value);
                }}
                placeholder="Phone"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                className="h-9 rounded-lg text-sm"
                data-testid="pos-cust-phone"
              />
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-semibold">{money(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Bill discount</span>
            <div className="flex items-center gap-1">
              ₹
              <Input
                type="number"
                value={billDiscount || ""}
                onChange={(e) => setBillDiscount(e.target.value)}
                className="h-7 w-20 rounded-lg text-right text-sm"
                data-testid="pos-discount"
              />
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              GST (
              {settings.gst_enabled === true
                ? Number(settings.gst_rate ?? 0)
                : 0}
              %)
            </span>
            <span className="font-semibold">{moneyExact(tax)}</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-dashed border-border">
            <span className="font-display font-bold">Total</span>
            <span
              className="font-display text-xl font-bold text-primary"
              data-testid="pos-total"
            >
              {moneyExact(total)}
            </span>
          </div>
          <div
            className={`grid ${
              isStarterPlan ? "grid-cols-1" : "grid-cols-2"
            } gap-2 pt-1`}
          >
            {!isStarterPlan && (
              <Button
                variant="outline"
                onClick={sendToKitchen}
                disabled={placing || !cart.length}
                className="h-11 rounded-xl font-semibold"
                data-testid="pos-primary-action"
              >
                {placing ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : existingOrder ? (
                  "Add Items & Send"
                ) : (
                  "Send to Kitchen"
                )}
              </Button>
            )}

            <Button
              onClick={() => {
                if (existingOrder) {
                  if (!existingOrderServed) {
                    toast.error("Dine-in order must be served before billing.");
                    return;
                  }

                  if (cart.length) {
                    toast.error(
                      "Please send the new items to the kitchen first.",
                    );
                    return;
                  }

                  billExistingOrder();
                  return;
                }

                // New Dine-In order must go to kitchen first.
                if (!isStarterPlan && type === "dine_in") {
                  return;
                }

                if (validate()) {
                  setPayOpen(true);
                }
              }}
              disabled={
                placing ||
                (existingOrder
                  ? !canBillExistingOrder
                  : !cart.length || (!isStarterPlan && type === "dine_in"))
              }
              className={cn(
                "rounded-xl font-semibold shadow-[0_4px_12px_rgba(234,88,12,0.3)]",
                existingOrder && cart.length
                  ? "h-auto min-h-11 whitespace-normal px-2 py-2 text-sm leading-tight text-center"
                  : "h-11 px-4",
              )}
              data-testid="pos-charge"
            >
              {existingOrder
                ? cart.length
                  ? "Send Items to Kitchen First"
                  : existingOrderServed
                    ? "Bill Existing Order"
                    : "Bill After Served"
                : isStarterPlan
                  ? `Charge ${moneyExact(total)}`
                  : type === "dine_in"
                    ? "Bill After Dining"
                    : `Charge ${moneyExact(total)}`}
            </Button>
          </div>
        </div>
      </div>

      <PaymentDialog
        open={payOpen}
        setOpen={setPayOpen}
        total={existingOrder ? existingOrderTotal : total}
        grossTotal={existingOrder ? existingOrderTotal : grossTotal}
        buildBody={buildBody}
        onDone={() => {
          resetCart();
          setExistingOrder(null);
          setAddItemMode(false);

          navigate("/app/pos", {
            replace: true,
            state: null,
          });
        }}
        settings={settings}
        existingOrder={existingOrder}
        restaurantName={restaurant?.name}
        restaurantAddress={activeBranchDetails?.address || ""}
        restaurantPhone={activeBranchDetails?.phone || ""}
        restaurantTagline={restaurant?.tagline || ""}
      />
    </div>
  );
}

const CatBtn = ({ active, children, ...p }) => (
  <button
    {...p}
    className={cn(
      "shrink-0 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors",
      active
        ? "bg-primary text-white"
        : "bg-secondary/70 text-foreground hover:bg-secondary",
    )}
  >
    {children}
  </button>
);
